import * as Sentry from "@sentry/react";
import { track, trackWithBeacon } from "client/src/utils/analytics";
import { useCallback, useEffect, useRef } from "react";
import type { AuthFailure, AuthFailureReason } from "client/src/auth/authApiErrorToAuthFailure";
import type { Factor } from "client/src/auth/oktaAuth";

// Enrollment: select, change_factor, enroll, activate, resend. Challenge: send, verify, resend.
export type LoginFlowStep =
  | "select"
  | "change_factor"
  | "enroll"
  | "activate"
  | "send"
  | "verify"
  | "resend";

type LoginFlowPhase = "credentials" | "mfa_enrollment" | "mfa_challenge" | "post_login";

type LoginFlow = {
  flowId: string;
  startedAt: number;
  phase: LoginFlowPhase;
  oktaUserId?: string;
  factorType?: Factor["factorType"];
  rememberDevice?: boolean;
  initialStatus?: "MFA_ENROLL" | "MFA_ENROLL_ACTIVATE";
  isEnrolling: boolean;
};

// `failure` is the already converted error shown to the user; `cause` is the original error.
const toErrorProperties = (failure: AuthFailure, cause: unknown) => ({
  errorCode:
    typeof cause === "object" &&
    cause !== null &&
    "errorCode" in cause &&
    typeof cause.errorCode === "string" &&
    /^E\d{7}$/.test(cause.errorCode)
      ? cause.errorCode
      : "unknown",
  errorSummary: failure.message,
  ...(failure.kind === "general" && failure.reason ? { reason: failure.reason } : {}),
});

const toEventProperties = (
  status: "started" | "step_submitted" | "step_failed" | "completed" | "cancelled" | "page_left",
  flow: LoginFlow,
  properties?: {
    phase?: LoginFlowPhase;
    step?: LoginFlowStep;
    reason?: "back_to_sign_in" | AuthFailureReason;
    errorCode?: string;
    errorSummary?: string;
    offeredFactorTypes?: string;
  },
) => {
  const { startedAt, isEnrolling, ...flowProperties } = flow;
  return {
    ...flowProperties,
    elapsedMs: Math.max(0, Date.now() - startedAt),
    status,
    ...properties,
  };
};

const report = (...args: Parameters<typeof toEventProperties>) => {
  void track("Login Flow", toEventProperties(...args)).catch((error: unknown) =>
    Sentry.captureException(error),
  );
};

export const useLoginFlowAnalytics = () => {
  const flow = useRef<LoginFlow | undefined>(undefined);

  // Closing, reloading, or navigating away from the page ends the flow without other rows.
  // The flow stays, because a page restored from the back-forward cache can continue it.
  useEffect(() => {
    const onPageHide = () => {
      if (flow.current) trackWithBeacon("Login Flow", toEventProperties("page_left", flow.current));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  const start = useCallback(() => {
    flow.current = {
      flowId: crypto.randomUUID(),
      startedAt: Date.now(),
      phase: "credentials",
      isEnrolling: false,
    };
    report("started", flow.current);
  }, []);

  const update = useCallback(
    (
      phase: LoginFlowPhase,
      {
        oktaUserId,
        factorType,
        rememberDevice,
      }: {
        oktaUserId?: unknown;
        factorType?: Factor["factorType"];
        rememberDevice?: boolean;
      } = {},
    ) => {
      if (!flow.current) return;
      flow.current.phase = phase;
      if (typeof oktaUserId === "string") flow.current.oktaUserId = oktaUserId;
      if (factorType) flow.current.factorType = factorType;
      if (rememberDevice !== undefined) flow.current.rememberDevice = rememberDevice;
    },
    [],
  );

  const startEnrollment = useCallback(
    (
      initialStatus: NonNullable<LoginFlow["initialStatus"]>,
      {
        oktaUserId,
        factorType,
        offeredFactors,
      }: {
        oktaUserId?: unknown;
        factorType?: Factor["factorType"];
        offeredFactors?: Factor[];
      } = {},
    ) => {
      if (!flow.current) return;
      if (typeof oktaUserId === "string") flow.current.oktaUserId = oktaUserId;
      if (factorType) flow.current.factorType = factorType;
      if (flow.current.isEnrolling) return;
      flow.current.phase = "mfa_enrollment";
      flow.current.isEnrolling = true;
      flow.current.initialStatus = initialStatus;
      report(
        "started",
        flow.current,
        offeredFactors
          ? {
              offeredFactorTypes: offeredFactors
                .map((factor) => factor.factorType)
                .sort()
                .join(","),
            }
          : undefined,
      );
    },
    [],
  );

  const submitStep = useCallback((step: LoginFlowStep, factorType?: Factor["factorType"]) => {
    if (!flow.current) return;
    if (factorType) flow.current.factorType = factorType;
    report("step_submitted", flow.current, {
      phase: flow.current.isEnrolling ? "mfa_enrollment" : "mfa_challenge",
      step,
    });
  }, []);

  const failStep = useCallback((step: LoginFlowStep, failure: AuthFailure, cause?: unknown) => {
    if (!flow.current) return;
    const isEnrollmentStep = flow.current.isEnrolling;
    report("step_failed", flow.current, {
      phase: isEnrollmentStep ? "mfa_enrollment" : flow.current.phase,
      ...(isEnrollmentStep || flow.current.phase === "mfa_challenge" ? { step } : {}),
      ...toErrorProperties(failure, cause),
    });
  }, []);

  const failLogin = useCallback((failure: AuthFailure, cause?: unknown) => {
    if (!flow.current) return;
    report("step_failed", flow.current, toErrorProperties(failure, cause));
  }, []);

  const completeEnrollment = useCallback(() => {
    if (!flow.current?.isEnrolling) return;
    report("completed", flow.current, { phase: "mfa_enrollment" });
    flow.current.isEnrolling = false;
  }, []);

  const completeLogin = useCallback(() => {
    if (!flow.current) return;
    flow.current.phase = "post_login";
    report("completed", flow.current);
    flow.current = undefined;
  }, []);

  const cancel = useCallback(() => {
    if (!flow.current) return;
    report("cancelled", flow.current, { reason: "back_to_sign_in" });
    flow.current = undefined;
  }, []);

  return {
    start,
    update,
    startEnrollment,
    submitStep,
    failStep,
    failLogin,
    completeEnrollment,
    completeLogin,
    cancel,
  };
};
